import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Permission, Role } from '../models/user.models';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/roles`;

  /**
   * Get all roles from the API
   */
  getAllRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(this.apiUrl).pipe(
      map((roles) => roles.map((role) => this.transformRole(role))),
    );
  }

  /**
   * Get all available permissions from the API
   */
  getAllPermissions(): Observable<Permission[]> {
    return this.http
      .get<Permission[]>(`${this.apiUrl}/permissions`)
      .pipe(
        map((permissions) =>
          permissions.map((p) => ({
            id: p.id,
            name: p.name,
            description: p.description ?? '',
            category: p.category ?? null,
            isActive: p.isActive ?? true,
            sortOrder: p.sortOrder ?? 0,
          })),
        ),
      );
  }

  /**
   * Get a specific role by ID
   */
  getRoleById(id: number): Observable<Role> {
    return this.http.get<Role>(`${this.apiUrl}/${id}`).pipe(
      map((role) => this.transformRole(role)),
    );
  }

  /**
   * Create a new role
   */
  createRole(
    role: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>,
  ): Observable<Role> {
    return this.http.post<Role>(this.apiUrl, role).pipe(
      map((role) => this.transformRole(role)),
    );
  }

  /**
   * Update an existing role
   */
  updateRole(id: number, role: Partial<Role>): Observable<Role> {
    return this.http.put<Role>(`${this.apiUrl}/${id}`, { ...role, id }).pipe(
      map((role) => this.transformRole(role)),
    );
  }

  /**
   * Delete a role
   */
  deleteRole(id: number): Observable<boolean> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      map(() => true),
    );
  }

  /**
   * Transform role data from API format to frontend format
   */
  private transformRole(role: any): Role {
    return {
      id: role.id,
      name: role.name || '',
      description: role.description,
      createdAt: role.createdAt,
      updatedAt: role.updatedAt,
      isActive: role.isActive ?? true,
      isDefaultUser: role.isDefaultUser ?? false,
      isProtectedAdmin: role.isProtectedAdmin ?? false,
      permissions: Array.isArray(role.permissions)
        ? role.permissions.map((p: any) => ({
            id: p.id,
            name: p.name,
            description: p.description ?? '',
            category: p.category ?? null,
            isActive: p.isActive ?? true,
            sortOrder: p.sortOrder ?? 0,
          }))
        : [],
    };
  }
}
